// components/OrderViewer/OrderViewer.tsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Pedido,
  Selection,
  ModifiedSelection,
  DeletedSelection,
  OwnerChange,
  SplittedSelectionPayload
} from './types';
import { formatCurrency } from '../../../../../utils/functions';
import { Button } from 'react-bootstrap';
import { updatePedidoSelectionsV2 } from '../../../../../crud/pedidos_api/pedidos';
import { getAutoPrintCancellationComandas } from '../../../../../crud/localDB/pedidosConfig';
import { FaSave, FaUndo } from 'react-icons/fa';
import { FaReceipt, FaArrowRightArrowLeft } from 'react-icons/fa6';
import GeneralView from './components/GeneralView/GeneralView';
import ClientView from './components/ClientView/ClientView';
import MoveSelectionsModal from './components/MoveSelectionsModal/MoveSelectionsModal';
import ModificationReasonModal from './components/ModificationReasonModal/ModificationReasonModal';
import { toast } from 'react-toastify';
import './OrderViewer.scss';
import { FaUser, FaUserGroup, FaSquareCheck } from 'react-icons/fa6';

interface OrderViewerProps {
  pedidosList: any[];
  allowEdit: boolean;
  mesa?: any; // Optional - only required for mesa mode, not for pedidos mode
  onClose?: () => void;
  setBlocked?: (blocked: boolean) => void;
  onGenerateReceipt?: (targetSelections: string[]) => void; // NEW: Callback when generating receipt for selected items
  onSelectionModeChange?: (isSelectionMode: boolean) => void; // NEW: Notify parent when selection mode changes
  pedido?: any; // Optional pedido object for delivery/pickup mode
  onMesaClosed?: (mesaId: number) => void; // NEW: Callback when mesa is closed
}

// Local helper type to tag new split rows in UI
type SelectionWithSplit = Selection & { split_origin_id?: string };

const OrderViewer: React.FC<OrderViewerProps> = ({
  pedidosList,
  allowEdit,
  mesa,
  onClose,
  setBlocked,
  onGenerateReceipt,
  onSelectionModeChange,
  pedido,
  onMesaClosed
}) => {
  const [modifiedPedidos, setModifiedPedidos] = useState<Pedido[]>([]);
  const initialPedidosRef = useRef<Pedido[]>([]);
  const [modifiedSelections, setModifiedSelections] = useState<ModifiedSelection[]>([]);
  const [deletedSelections, setDeletedSelections] = useState<DeletedSelection[]>([]);
  const [ownerChanges, setOwnerChanges] = useState<OwnerChange[]>([]);
  const [splittedSelections, setSplittedSelections] = useState<SplittedSelectionPayload[]>([]);
  const [viewMode, setViewMode] = useState<'todo' | 'porPersona' | 'seleccionar'>('todo');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set()); // pedido_id:selection_id
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showModificationReasonModal, setShowModificationReasonModal] = useState(false);
  const [printToKitchen, setPrintToKitchen] = useState(false);
  const [autoPrintCancellationComandas, setAutoPrintCancellationComandas] = useState(false);

  // ---------- Helpers ----------

  // find current pedido/selection in local state
  const getCurrentSel = (pedido_id: number, selection_id: string) => {
    const pedido = modifiedPedidos.find(p => p.pedido_id === pedido_id);
    if (!pedido) return { pedido: undefined as unknown as Pedido, sel: undefined as unknown as SelectionWithSplit };
    const sel = pedido.selections.find(s => s.selection_id === selection_id) as SelectionWithSplit | undefined;
    return { pedido, sel };
  };

  // whether a selection existed in the original baseline (not a newly split child)
  const isBaselineSelection = (pedido_id: number, selection_id: string) => {
    const p = pedidosList.find(p => p.pedido_id === pedido_id);
    return !!p?.selections.find((s: any) => s.selection_id === selection_id);
  };

  // pure helper to recalc total by unit price
  const withQtyChange = <T extends Selection>(sel: T, nextQty: number): T => {
    const unit = sel.quantity > 0 ? sel.total_price / sel.quantity : 0;
    const q = Math.max(0, nextQty);
    return {
      ...sel,
      quantity: q,
      total_price: unit * q,
    };
  };

  // upsert a split payload entry (by pedido & original_selection_id)
  const upsertSplitPayload = (
    pedido_id: number,
    original_selection_id: string,
    original_selection_quantity: number,
    updater: (entry: SplittedSelectionPayload) => void
  ) => {
    setSplittedSelections(prev => {
      const idx = prev.findIndex(
        s => s.pedido_id === pedido_id && s.original_selection_id === original_selection_id
      );
      if (idx === -1) {
        const entry: SplittedSelectionPayload = {
          pedido_id,
          original_selection_id,
          original_selection_quantity,
          new_selections: [],
        };
        updater(entry);
        return [...prev, entry];
      } else {
        const copy = [...prev];
        const entry = { ...copy[idx] };
        updater(entry);
        // remove if emptied by updater
        if (!entry.new_selections || entry.new_selections.length === 0) {
          copy.splice(idx, 1);
          return copy;
        }
        copy[idx] = entry;
        return copy;
      }
    });
  };

  // Carga inicial de pedidos y reseteo de cambios
  useEffect(() => {
    const pedidosCopy: Pedido[] = pedidosList.map(pedido => ({
      ...pedido,
      selections: pedido.selections.map((sel: any) => ({
        ...sel,
        total_price: sel.total_price,
      })),
    }));
    initialPedidosRef.current = pedidosCopy;
    setModifiedPedidos(pedidosCopy);
    setModifiedSelections([]);
    setDeletedSelections([]);
    setOwnerChanges([]);
    setSplittedSelections([]);
  }, [pedidosList]);

  useEffect(() => {
    let isMounted = true;

    getAutoPrintCancellationComandas()
      .then((value) => {
        if (!isMounted) return;
        setAutoPrintCancellationComandas(value);
        if (value) {
          setPrintToKitchen(true);
        }
      })
      .catch((error) => {
        console.error('Error loading cancellation comanda config:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // ---------- Quantity Handlers (preserve total with split children) ----------

  // Aumentar cantidad
  const handleIncreaseQuantity = (pedido_id: number, selection_id: string) => {
    const baseline = isBaselineSelection(pedido_id, selection_id);
    const { sel: curSel } = getCurrentSel(pedido_id, selection_id);
    if (!curSel) return;

    const isSplitChild = !!curSel.split_origin_id;
    const originId = curSel.split_origin_id;

    // 1) Update UI state
    setModifiedPedidos(prev =>
      prev.map(p =>
        p.pedido_id !== pedido_id
          ? p
          : {
              ...p,
              selections: p.selections
                .map(s => {
                  // bump selected row
                  if (s.selection_id === selection_id) {
                    return withQtyChange(s, s.quantity + 1);
                  }
                  // if split child: take 1 from origin (can go to 0 and be filtered)
                  if (isSplitChild && originId && s.selection_id === originId) {
                    return withQtyChange(s, s.quantity - 1);
                  }
                  return s;
                })
                .filter(s => s.quantity > 0),
            }
      )
    );

    // 2) Track deltas
    if (baseline && !isSplitChild) {
      // baseline row behaves like before
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSel = originalPedido.selections.find((s: any) => s.selection_id === selection_id)!;
      setModifiedSelections(prev => {
        const exist = prev.find(ms => ms.pedido_id === pedido_id && ms.selection_id === selection_id);
        if (exist) {
          return prev.map(ms =>
            ms.pedido_id === pedido_id && ms.selection_id === selection_id
              ? { ...ms, new_quantity: ms.new_quantity + 1 }
              : ms
          );
        }
        return [
          ...prev,
          {
            pedido_id,
            selection_id,
            old_quantity: originalSel.quantity,
            new_quantity: originalSel.quantity + 1,
          },
        ];
      });
    } else if (isSplitChild && originId) {
      // child +1 => reflect in split payload (child quantity +1 under that origin)
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSelBaseline = originalPedido.selections.find((s: any) => s.selection_id === originId)!;

      upsertSplitPayload(
        pedido_id,
        originId,
        originalSelBaseline.quantity,
        (entry) => {
          const idx = entry.new_selections.findIndex(ns => ns.selection_id === selection_id);
          if (idx === -1) {
            entry.new_selections.push({
              selection_id,
              quantity: 1,
              client_index: (curSel as any).clientIndex ?? -1,
            } as any);
          } else {
            const child = { ...entry.new_selections[idx] };
            child.quantity = (child.quantity ?? 0) + 1;
            entry.new_selections[idx] = child;
          }
        }
      );
    }
  };

  // Disminuir cantidad
  const handleDecreaseQuantity = (pedido_id: number, selection_id: string) => {
    const baseline = isBaselineSelection(pedido_id, selection_id);
    const { sel: curSel } = getCurrentSel(pedido_id, selection_id);
    if (!curSel) return;

    const isSplitChild = !!curSel.split_origin_id;
    const originId = curSel.split_origin_id;

    // 1) Update UI state (child -1 => origin +1)
    setModifiedPedidos(prev =>
      prev.map(p =>
        p.pedido_id !== pedido_id
          ? p
          : {
              ...p,
              selections: p.selections
                .map(s => {
                  if (s.selection_id === selection_id) {
                    // if going from 1 -> 0, we’ll filter out below
                    return withQtyChange(s, s.quantity - 1);
                  }
                  if (isSplitChild && originId && s.selection_id === originId) {
                    return withQtyChange(s, s.quantity + 1);
                  }
                  return s;
                })
                .filter(s => !(s.selection_id === selection_id && (curSel.quantity <= 1))) // drop if went to 0
            }
      )
    );

    // 2) Track deltas
    if (baseline && !isSplitChild) {
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSel = originalPedido.selections.find((s: any) => s.selection_id === selection_id)!;
      setModifiedSelections(prev => {
        const exist = prev.find(ms => ms.pedido_id === pedido_id && ms.selection_id === selection_id);
        if (exist) {
          const newQty = exist.new_quantity - 1;
          if (newQty === exist.old_quantity) {
            return prev.filter(ms => !(ms.pedido_id === pedido_id && ms.selection_id === selection_id));
          }
          if (newQty <= 0) {
            return prev.filter(ms => !(ms.pedido_id === pedido_id && ms.selection_id === selection_id));
          }
          return prev.map(ms =>
            ms.pedido_id === pedido_id && ms.selection_id === selection_id
              ? { ...ms, new_quantity: newQty }
              : ms
          );
        } else if (originalSel.quantity > 1) {
          return [
            ...prev,
            {
              pedido_id,
              selection_id,
              old_quantity: originalSel.quantity,
              new_quantity: originalSel.quantity - 1,
            },
          ];
        }
        return prev;
      });
    } else if (isSplitChild && originId) {
      // child -1 => reflect in split payload
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSelBaseline = originalPedido.selections.find((s: any) => s.selection_id === originId)!;

      upsertSplitPayload(
        pedido_id,
        originId,
        originalSelBaseline.quantity,
        (entry) => {
          const idx = entry.new_selections.findIndex(ns => ns.selection_id === selection_id);
          if (idx === -1) return; // nothing to do
          const child = { ...entry.new_selections[idx] };
          child.quantity = Math.max(0, (child.quantity ?? 0) - 1);
          if (child.quantity === 0) {
            entry.new_selections.splice(idx, 1);
          } else {
            entry.new_selections[idx] = child;
          }
        }
      );
    }
  };

  // Eliminar selección
  const handleDeleteSelection = (pedido_id: number, selection_id: string) => {
    const { sel: curSel } = getCurrentSel(pedido_id, selection_id);
    if (!curSel) return;

    const isSplitChild = !!curSel.split_origin_id;
    const originId = curSel.split_origin_id;

    if (isSplitChild && originId) {
      // Deleting a child created by split: return its qty to origin and remove child from split payload
      const childQty = curSel.quantity;

      setModifiedPedidos(prev =>
        prev.map(p =>
          p.pedido_id !== pedido_id
            ? p
            : {
                ...p,
                selections: p.selections
                  .map(s => {
                    if (s.selection_id === originId) {
                      return withQtyChange(s, s.quantity + childQty);
                    }
                    return s;
                  })
                  .filter(s => s.selection_id !== selection_id),
              }
        )
      );

      // Remove this child from the split payload
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSelBaseline = originalPedido.selections.find((s: any) => s.selection_id === originId)!;

      upsertSplitPayload(
        pedido_id,
        originId,
        originalSelBaseline.quantity,
        (entry) => {
          entry.new_selections = entry.new_selections.filter(ns => ns.selection_id !== selection_id);
        }
      );

      // No deletion record sent to backend for child that doesn't exist there yet
      setModifiedSelections(prev =>
        prev.filter(ms => !(ms.pedido_id === pedido_id && ms.selection_id === selection_id))
      );
      setOwnerChanges(prev =>
        prev.filter(oc => !(oc.pedido_id === pedido_id && oc.selection_id === selection_id))
      );
      return;
    }

    // Baseline deletion (original behavior)
    setModifiedPedidos(prev =>
      prev.map(pedido =>
        pedido.pedido_id === pedido_id
          ? { ...pedido, selections: pedido.selections.filter(sel => sel.selection_id !== selection_id) }
          : pedido
      )
    );
    setDeletedSelections(prev =>
      prev.some(ds => ds.pedido_id === pedido_id && ds.selection_id === selection_id)
        ? prev
        : [...prev, { pedido_id, selection_id }]
    );
    setModifiedSelections(prev =>
      prev.filter(ms => !(ms.pedido_id === pedido_id && ms.selection_id === selection_id))
    );
    setOwnerChanges(prev =>
      prev.filter(oc => !(oc.pedido_id === pedido_id && oc.selection_id === selection_id))
    );
  };

  // Cambiar dueño (cliente) – actualiza payload de splits si es hijo
  const handleChangeOwner = (
    pedido_id: number,
    selection_id: string,
    new_client_index: number
  ) => {
    const { sel: modifiedSel } = getCurrentSel(pedido_id, selection_id);
    if (!modifiedSel) return;

    // Si ya está asignado al mismo cliente en el estado actual, no hacemos nada
    if (modifiedSel.clientIndex === new_client_index) return;

    // 1) Actualizamos el estado local
    setModifiedPedidos(prev =>
      prev.map(pedido =>
        pedido.pedido_id === pedido_id
          ? {
              ...pedido,
              selections: pedido.selections.map(sel =>
                sel.selection_id === selection_id
                  ? { ...sel, clientIndex: new_client_index }
                  : sel
              ),
            }
          : pedido
      )
    );

    const isSplitChild = !!modifiedSel.split_origin_id;
    const originId = modifiedSel.split_origin_id;

    if (isSplitChild && originId) {
      // actualizar el client_index del hijo dentro de splittedSelections
      const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
      const originalSelBaseline = originalPedido.selections.find((s: any) => s.selection_id === originId)!;

      upsertSplitPayload(
        pedido_id,
        originId,
        originalSelBaseline.quantity,
        (entry) => {
          const idx = entry.new_selections.findIndex(ns => ns.selection_id === selection_id);
          if (idx !== -1) {
            entry.new_selections[idx] = {
              ...entry.new_selections[idx],
              client_index: new_client_index,
            } as any;
          } else {
            // If not found (shouldn't happen), add it with qty=modifiedSel.quantity
            entry.new_selections.push({
              selection_id,
              quantity: modifiedSel.quantity,
              client_index: new_client_index,
            } as any);
          }
        }
      );

      // Do NOT add to ownerChanges (child doesn't exist in backend yet)
      return;
    }

    // 2) Registramos o eliminamos el cambio en ownerChanges (baseline)
    const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
    const originalSel = originalPedido.selections.find((s: any) => s.selection_id === selection_id)!;

    setOwnerChanges(prev => {
      // Si vuelve al índice original, quitamos el registro
      if (new_client_index === originalSel.clientIndex) {
        return prev.filter(oc => !(oc.pedido_id === pedido_id && oc.selection_id === selection_id));
      }
      const exist = prev.find(oc => oc.pedido_id === pedido_id && oc.selection_id === selection_id);
      if (exist) {
        return prev.map(oc =>
          oc.pedido_id === pedido_id && oc.selection_id === selection_id
            ? { ...oc, new_client_index }
            : oc
        );
      }
      return [
        ...prev,
        {
          pedido_id,
          selection_id,
          previous_client_index: originalSel.clientIndex,
          new_client_index,
        },
      ];
    });
  };

  // Separar selección (split)
  const handleSplitSelection = (pedido_id: number, selection_id: string) => {
    const originalPedido = pedidosList.find(p => p.pedido_id === pedido_id)!;
    const originalSelBaseline = originalPedido.selections.find((s: any) => s.selection_id === selection_id)!;

    const modifiedPedido = modifiedPedidos.find(p => p.pedido_id === pedido_id)!;
    const modifiedSel = modifiedPedido.selections.find(s => s.selection_id === selection_id)! as SelectionWithSplit;

    if (modifiedSel.quantity <= 1) return; // No se puede separar si solo hay 1

    // Generar nuevo UUID para la nueva selección
    const newSelectionId = crypto.randomUUID();

    // Precio unitario
    const unit = modifiedSel.total_price / modifiedSel.quantity;

    // Crear nueva selección con cantidad 1 y marcar origen
    const newSelection: SelectionWithSplit = {
      ...modifiedSel,
      selection_id: newSelectionId,
      quantity: 1,
      total_price: unit * 1,
      split_origin_id: selection_id,
    };

    // 1) Actualizar el estado local (decrementar origen, agregar hijo)
    setModifiedPedidos(prev =>
      prev.map(p =>
        p.pedido_id !== pedido_id
          ? p
          : {
              ...p,
              selections: [
                ...p.selections
                  .map(sel =>
                    sel.selection_id === selection_id
                      ? withQtyChange(sel, sel.quantity - 1)
                      : sel
                  )
                  .filter(sel => sel.quantity > 0),
                newSelection
              ],
            }
      )
    );

    // 2) NO registrar en modifiedSelections; registramos en splittedSelections
    upsertSplitPayload(
      pedido_id,
      selection_id,
      originalSelBaseline.quantity,
      (entry) => {
        entry.new_selections.push({
          selection_id: newSelectionId,
          quantity: 1,
          client_index: (newSelection as any).clientIndex ?? -1,
        } as any);
      }
    );

    toast.info('Selección separada');
  };

  // Helper: Check if there are any deletions (deleted selections or decreased quantities)
  const hasDeletions = (): boolean => {
    // Check explicit deletions
    if (deletedSelections.length > 0) return true;
    
    // Check modified selections with decreased quantities (quantity decrease = partial deletion)
    const hasDecreasedQuantities = modifiedSelections.some(ms => ms.new_quantity < ms.old_quantity);
    
    return hasDecreasedQuantities;
  };

  // Count total number of items being deleted (for modal display)
  const getDeletionsCount = (): number => {
    let count = deletedSelections.length;
    
    // Add decreased quantities count
    modifiedSelections.forEach(ms => {
      if (ms.new_quantity < ms.old_quantity) {
        count += (ms.old_quantity - ms.new_quantity);
      }
    });
    
    return count;
  };

  // Check if confirming modifications will result in mesa closure
  const willCloseMesa = (): boolean => {
    if (!mesa) return false;
    
    // Simulate what the state would be after modifications
    const afterModifications = modifiedPedidos.map(pedido => ({
      ...pedido,
      selections: pedido.selections.filter(sel => {
        // Check if this selection will be deleted
        const isDeleted = deletedSelections.some(
          ds => ds.pedido_id === pedido.pedido_id && ds.selection_id === sel.selection_id
        );
        if (isDeleted) return false;
        
        // Check if this selection will be reduced to 0
        const modification = modifiedSelections.find(
          ms => ms.pedido_id === pedido.pedido_id && ms.selection_id === sel.selection_id
        );
        if (modification && modification.new_quantity <= 0) return false;
        
        return true;
      })
    }));
    
    // Check if all pedidos would have no selections
    return afterModifications.every(p => p.selections.length === 0);
  };

  // Trigger confirmation flow - show modal if deletions exist
  const handleInitiateConfirmation = () => {
    if (hasDeletions()) {
      setShowModificationReasonModal(true);
    } else {
      // No deletions, confirm directly without reason
      confirmModifications();
    }
  };

  // Actual confirmation after reason is selected (or no deletions)
  const confirmModifications = async (payload?: { reasonId?: number; comment?: string }) => {
    try {
      await updatePedidoSelectionsV2(
        deletedSelections, 
        modifiedSelections, 
        ownerChanges, 
        splittedSelections,
        payload?.comment,
        payload?.reasonId,
        autoPrintCancellationComandas || printToKitchen
      );
      toast.success('Modificaciones confirmadas');
      setShowModificationReasonModal(false);
      setModifiedSelections([]);
      setDeletedSelections([]);
      setOwnerChanges([]);
      setSplittedSelections([]);
      
      // Update the baseline reference to current state after successful confirmation
      // This prevents "undo" from restoring already-deleted items
      const updatedPedidos: Pedido[] = modifiedPedidos.map(pedido => ({
        ...pedido,
        selections: pedido.selections.map((sel: any) => ({
          ...sel,
          total_price: sel.total_price,
        })),
      }));
      initialPedidosRef.current = updatedPedidos;
      
      // Check if all selections are now empty and close the mesa if needed
      if (mesa && updatedPedidos.length && updatedPedidos.every(p => p.selections.length === 0)) {
        console.log('[OrderViewer] Mesa will be closed, calling onMesaClosed for mesa:', mesa.id);
        mesa.used = false;
        // Notify parent that mesa is being closed for real-time visual update
        if (onMesaClosed && mesa.id) {
          console.log('[OrderViewer] Executing onMesaClosed callback for mesa:', mesa.id);
          onMesaClosed(mesa.id);
        } else {
          console.warn('[OrderViewer] onMesaClosed callback not available or mesa.id missing', { onMesaClosed: !!onMesaClosed, mesaId: mesa.id });
        }
        onClose?.();
      } else {
        console.log('[OrderViewer] Mesa will NOT be closed', {
          hasMesa: !!mesa,
          updatedPedidosLength: updatedPedidos.length,
          allSelectionsEmpty: updatedPedidos.every(p => p.selections.length === 0),
          selectionsCount: updatedPedidos.reduce((sum, p) => sum + p.selections.length, 0)
        });
      }
    } catch (error) {
      toast.error('Error confirmando modificaciones');
      throw error; // Re-throw to handle in modal
    }
  };

  // Deshacer todo
  const undoAllChanges = () => {
    setModifiedPedidos(initialPedidosRef.current);
    setModifiedSelections([]);
    setDeletedSelections([]);
    setOwnerChanges([]);
    setSplittedSelections([]);
    toast.info('Todos los cambios han sido deshechos');
  };

  // Bloquear/desbloquear mesa según la visibilidad de los botones de confirmación
  useEffect(() => {
    if (typeof setBlocked === 'function') {
      if (
        modifiedSelections.length > 0 ||
        deletedSelections.length > 0 ||
        ownerChanges.length > 0 ||
        splittedSelections.length > 0
      ) {
        setBlocked(true);
      } else {
        setBlocked(false);
      }
    }
  }, [modifiedSelections, deletedSelections, ownerChanges, splittedSelections, setBlocked]);

  // Agregar la propiedad pedido y agrupar
  const aggregatedSelections: (SelectionWithSplit & { pedido: Pedido })[] = modifiedPedidos.flatMap(pedido =>
    pedido.selections.map(sel => ({ ...(sel as SelectionWithSplit), pedido }))
  );
  
  // Calculate total excluding paid selections (only pending items)
  const totalPrice = aggregatedSelections.reduce((sum, sel) => {
    // Skip paid selections from total
    if (sel.payed === true) return sum;
    return sum + sel.total_price;
  }, 0);

  const selectionsByClient: { [clientIndex: number]: (SelectionWithSplit & { pedido: Pedido })[] } = {};
  aggregatedSelections.forEach(sel => {
    const idx = (sel as any).clientIndex;
    if (!selectionsByClient[idx]) selectionsByClient[idx] = [];
    selectionsByClient[idx].push(sel);
  });

  // Notify parent when selection mode changes
  useEffect(() => {
    if (onSelectionModeChange) {
      onSelectionModeChange(viewMode === 'seleccionar');
    }
  }, [viewMode, onSelectionModeChange]);

  return (
    <div className="order-viewer-outer-container">
      {/* Botones de vista - Fixed header */}
      {!mesa?.is_virtual && (
        <div className="view-mode-buttons">
          <Button
            variant="none"
            className={`view-mode-button ${viewMode === 'todo' ? 'selected' : ''}`}
            onClick={() => {
              setViewMode('todo');
              setSelectedItems(new Set());
            }}
          >
            <FaUser className="toggle-icon" /> Todo
          </Button>
          <Button
            variant="none"
            className={`view-mode-button ${viewMode === 'porPersona' ? 'selected' : ''}`}
            onClick={() => {
              setViewMode('porPersona');
              setSelectedItems(new Set());
            }}
          >
            <FaUserGroup size={17} className="toggle-icon" /> Por Persona
          </Button>
          <Button
            variant="none"
            className={`view-mode-button ${viewMode === 'seleccionar' ? 'selected' : ''}`}
            onClick={() => {
              setViewMode('seleccionar');
              setSelectedItems(new Set());
            }}
          >
            <FaSquareCheck size={17} className="toggle-icon" /> Seleccionar
          </Button>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="order-viewer-content">
        <div className="order-viewer-container">
          {viewMode === 'todo' || viewMode === 'seleccionar' ? (
            <GeneralView
              selections={aggregatedSelections as any}
              allowEdit={allowEdit}
              handleIncreaseQuantity={handleIncreaseQuantity}
              handleDecreaseQuantity={handleDecreaseQuantity}
              handleChangeOwner={handleChangeOwner}
              handleDeleteSelection={handleDeleteSelection}
              handleSplitSelection={handleSplitSelection}
              isSelectionMode={viewMode === 'seleccionar'}
              selectedItems={selectedItems}
              onToggleSelection={(pedidoId, selectionId) => {
                const key = `${pedidoId}:${selectionId}`;
                setSelectedItems(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(key)) {
                    newSet.delete(key);
                  } else {

                    newSet.add(key);
                  }
                  return newSet;
                });
              }}
            />
          ) : (
            <ClientView
              selectionsByClient={selectionsByClient as any}
              allowEdit={allowEdit}
              handleIncreaseQuantity={handleIncreaseQuantity}
              handleDecreaseQuantity={handleDecreaseQuantity}
              handleChangeOwner={handleChangeOwner}
              handleDeleteSelection={handleDeleteSelection}
              handleSplitSelection={handleSplitSelection}
            />
          )}
        </div>
      </div>

      {/* Fixed footer area */}
      <div className="order-viewer-footer">
        {/* Selection Mode Actions */}
        {viewMode === 'seleccionar' && (
          <div className="selection-actions-bar">
            <Button 
              variant="none" 
              className="btn-cancel-selection"
              onClick={() => {
                setViewMode('todo');
                setSelectedItems(new Set());
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="none" 
              className="btn-generar-recibo"
              onClick={() => {
                if (selectedItems.size === 0) {
                  toast.warning('Selecciona al menos un ítem para cobrar');
                  return;
                }
                // Extract only selection_ids from the Set
                const selectionIds = Array.from(selectedItems).map(item => item.split(':')[1]);
                
                // Call the callback to trigger payment flow in MesaController
                if (onGenerateReceipt) {
                  onGenerateReceipt(selectionIds);
                }
                
                // Exit selection mode
                setViewMode('todo');
                setSelectedItems(new Set());
              }}
              disabled={selectedItems.size === 0}
            >
              <FaReceipt /> Generar Precuenta ({selectedItems.size})
            </Button>
            {/* Only show Move button when mesa exists (not in pedidos mode) */}
            {mesa && (
              <Button 
                variant="none" 
                className="btn-mover-selection"
                onClick={() => {
                  if (selectedItems.size === 0) {
                    toast.warning('Selecciona al menos un ítem para mover');
                    return;
                  }
                  setShowMoveModal(true);
                }}
                disabled={selectedItems.size === 0}
              >
                <FaArrowRightArrowLeft /> Mover ({selectedItems.size})
              </Button>
            )}
          </div>
        )}

        {/* Confirmar / Deshacer */}
        {viewMode !== 'seleccionar' && (modifiedSelections.length > 0 ||
          deletedSelections.length > 0 ||
          ownerChanges.length > 0 ||
          splittedSelections.length > 0) && (
          <div className="confirm-modifications">
            <Button variant="none" className="undo-all-changes-button" onClick={undoAllChanges}>
              <FaUndo /> Deshacer cambios
            </Button>
            <Button variant="none" className="confirm-modifications-button" onClick={handleInitiateConfirmation}>
              <FaSave /> Confirmar cambios
            </Button>
          </div>
        )}

        {/* Total Price - Hide in selection mode */}
        {viewMode !== 'seleccionar' && (
          <div className="total-price">
            <span>Total</span>
            <span>{formatCurrency(totalPrice)}</span>
          </div>
        )}
      </div>

      {/* Move Selections Modal */}
      <MoveSelectionsModal
        show={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setViewMode('todo');
          setSelectedItems(new Set());
        }}
        selectedItems={selectedItems}
        currentMesaId={mesa?.id}
        currentMesaNumber={mesa?.tag || mesa?.num || mesa?.mesa_number || 'N/A'}
      />

      {/* Modification Reason Modal */}
      <ModificationReasonModal
        show={showModificationReasonModal}
        onClose={() => setShowModificationReasonModal(false)}
        onConfirm={(payload) => confirmModifications(payload)}
        deletionsCount={getDeletionsCount()}
        willCloseMesa={willCloseMesa()}
        printToKitchen={printToKitchen}
        onPrintToKitchenChange={setPrintToKitchen}
        autoPrintToKitchen={autoPrintCancellationComandas}
      />
    </div>
  );
};

export default OrderViewer;
